import {
  FC,
  FormEvent,
  KeyboardEvent,
  ReactElement,
  useEffect,
  useRef,
  useState,
} from 'react';
import classNames from 'classnames';
import { DialogPanel, DialogTitle } from '@headlessui/react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCircleArrowUp,
  faCircleExclamation,
  faReply,
  faXmark,
} from '@fortawesome/pro-solid-svg-icons';
import { useFormatter, useNow, useTranslations } from 'next-intl';
import {
  Comment,
  CommentId,
  CommentSubject,
  conversationCount,
  isCommentEdited,
} from 'models/Comment';
import { useModal } from 'xstate/hooks/useModal';
import { closeModal } from 'xstate/actors/ModalActor';
import { CommentsModalPayload } from 'xstate/machines/ModalMachine';
import { commentsQuery } from 'networking/mindfeed/queries/comments';
import { memoQuery } from 'networking/mindfeed/queries/memo';
import type { AnyMemo } from 'models/Memo';
import useAuthenticatedUser from 'networking/mindfeed/hooks/useAuthenticatedUser';
import { useComment } from 'networking/mindfeed/hooks/useComment';
import { useCreateComment } from 'networking/mindfeed/hooks/useCreateComment';
import { useDeleteComment } from 'networking/mindfeed/hooks/useDeleteComment';
import { useMemoCache } from 'networking/mindfeed/hooks/useOptimisticMemoMutation';
import Spinner from 'elements/Spinner';
import { MediaAvatar, initialsOf } from 'elements/Avatar';

type Failure = 'load' | 'post' | 'delete';

// The conversation on a memo: top-level comments with their replies, and a
// composer pinned at the bottom. Opens seeded with the comments the feed
// already has, then reloads so other people's new comments show up.
const CommentsModal = (): ReactElement => {
  const t = useTranslations();
  const { payload } = useModal() as { payload: CommentsModalPayload };
  const memo = payload.memo;
  const subject: CommentSubject = { type: 'memo', id: memo.id };

  const { authenticatedUser } = useAuthenticatedUser();
  const {
    data: comments,
    isFetching,
    isError,
    refetch,
  } = useQuery({
    ...commentsQuery(subject),
    // Show the feed's copy straight away while the fresh list loads.
    placeholderData: memo.comments,
    refetchOnMount: 'always',
  });

  // Keep the card's chip honest while the modal is up: write the latest
  // listing back onto the memo in the feed's cache, and onto the memo shown
  // on its own (e.g. under a notification) if there is one.
  const cache = useMemoCache();
  const queryClient = useQueryClient();
  useEffect(() => {
    if (comments && comments !== memo.comments) {
      cache.update(memo.id, { comments });
      queryClient.setQueryData<AnyMemo>(memoQuery(memo.id).queryKey, (old) =>
        old ? { ...old, comments } : old,
      );
    }
  }, [cache, queryClient, comments, memo.id, memo.comments]);

  const [draft, setDraft] = useState('');
  const [replyingTo, setReplyingTo] = useState<CommentId>();
  const [expandedIds, setExpandedIds] = useState<Set<CommentId>>(new Set());
  const [failure, setFailure] = useState<Failure>();
  const composerRef = useRef<HTMLTextAreaElement>(null);

  const createComment = useCreateComment({
    onSuccess: (comment) => {
      setDraft('');
      setReplyingTo(undefined);
      // Show the thread the reply landed in.
      if (comment.parentId) expand(comment.parentId);
    },
  });
  const deleteComment = useDeleteComment();

  const expand = (id: CommentId) =>
    setExpandedIds((ids) => new Set(ids).add(id));

  const list = comments ?? [];
  const replyingToComment = list.find((c) => c.id === replyingTo);
  const canSubmit = draft.trim().length > 0 && !createComment.isPending;

  const submit = (event?: FormEvent) => {
    event?.preventDefault();
    const content = draft.trim();
    if (!content || createComment.isPending) return;
    setFailure(undefined);
    createComment.mutate(
      { subject, content, parentId: replyingTo },
      { onError: () => setFailure('post') },
    );
  };

  const onComposerKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      submit();
    }
  };

  const startReply = (comment: Comment) => {
    setReplyingTo(comment.id);
    composerRef.current?.focus();
  };

  const remove = (comment: Comment) => {
    setFailure(undefined);
    deleteComment.mutate(
      { subject, commentId: comment.id, parentId: comment.parentId },
      { onError: () => setFailure('delete') },
    );
  };

  const showLoadFailure = isError && list.length === 0;
  const bannerFailure =
    failure ?? (isError && list.length > 0 ? 'load' : undefined);
  const count = conversationCount(list);

  return (
    <div className={styles.outer}>
      <div className={styles.inner}>
        <DialogPanel transition className={styles.panel}>
          <div className={styles.header.container}>
            <div className={styles.header.titleWrap}>
              <DialogTitle as="h2" className={styles.header.title}>
                {t('comments.title')}
              </DialogTitle>
              {count > 0 && (
                <span className={styles.header.count}>{count}</span>
              )}
              {isFetching && <Spinner className="h-4 w-4 text-ink-secondary" />}
            </div>
            <button
              type="button"
              aria-label={t('common.close')}
              onClick={closeModal}
              className={styles.header.close}
            >
              <FontAwesomeIcon icon={faXmark} />
            </button>
          </div>

          <div className={styles.body}>
            {showLoadFailure ? (
              <div className={styles.empty.container}>
                <FontAwesomeIcon
                  icon={faCircleExclamation}
                  className={styles.empty.icon}
                />
                <p className={styles.empty.title}>{t('comments.errorLoad')}</p>
                <button
                  type="button"
                  className={styles.empty.retry}
                  onClick={() => refetch()}
                >
                  {t('common.retry')}
                </button>
              </div>
            ) : list.length === 0 ? (
              <div className={styles.empty.container}>
                {!isFetching && (
                  <>
                    <p className={styles.empty.title}>
                      {t('comments.emptyTitle')}
                    </p>
                    <p className={styles.empty.subtitle}>
                      {t('comments.emptySubtitle')}
                    </p>
                  </>
                )}
              </div>
            ) : (
              <ul className={styles.list}>
                {list.map((comment) => (
                  <CommentRow
                    key={comment.id}
                    comment={comment}
                    currentUserId={authenticatedUser?.id}
                    isExpanded={expandedIds.has(comment.id)}
                    onExpand={() => expand(comment.id)}
                    onReply={() => startReply(comment)}
                    onDelete={remove}
                  />
                ))}
              </ul>
            )}
          </div>

          {bannerFailure && (
            <div role="alert" className={styles.banner.container}>
              <FontAwesomeIcon
                icon={faCircleExclamation}
                className={styles.banner.icon}
              />
              <span className={styles.banner.text}>
                {bannerFailure === 'post'
                  ? t('comments.errorPost')
                  : bannerFailure === 'delete'
                    ? t('comments.errorDelete')
                    : t('comments.errorLoad')}
              </span>
              <button
                type="button"
                aria-label={t('common.dismiss')}
                className={styles.banner.close}
                onClick={() => setFailure(undefined)}
              >
                <FontAwesomeIcon icon={faXmark} />
              </button>
            </div>
          )}

          <form className={styles.composer.container} onSubmit={submit}>
            {replyingToComment && (
              <div className={styles.composer.replying}>
                <FontAwesomeIcon icon={faReply} className="text-[10px]" />
                <span className="truncate">
                  {t('comments.replyingTo', {
                    name: replyingToComment.author.displayName,
                  })}
                </span>
                <button
                  type="button"
                  aria-label={t('common.dismiss')}
                  className={styles.composer.cancelReply}
                  onClick={() => setReplyingTo(undefined)}
                >
                  <FontAwesomeIcon icon={faXmark} />
                </button>
              </div>
            )}
            <div className={styles.composer.row}>
              <textarea
                ref={composerRef}
                rows={1}
                value={draft}
                placeholder={t('comments.composerPlaceholder')}
                onChange={(event) => setDraft(event.target.value)}
                onKeyDown={onComposerKeyDown}
                className={styles.composer.input}
              />
              <button
                type="submit"
                aria-label={t('common.send')}
                disabled={!canSubmit}
                className={classNames(
                  styles.composer.send,
                  canSubmit ? 'text-accent-ink' : 'text-ink-tertiary',
                )}
              >
                {createComment.isPending ? (
                  <Spinner className="h-5 w-5" />
                ) : (
                  <FontAwesomeIcon
                    icon={faCircleArrowUp}
                    className="text-2xl"
                  />
                )}
              </button>
            </div>
          </form>
        </DialogPanel>
      </div>
    </div>
  );
};

export default CommentsModal;

interface RowProps {
  comment: Comment;
  currentUserId?: string;
  isExpanded: boolean;
  onExpand: () => void;
  onReply: () => void;
  onDelete: (comment: Comment) => void;
}

// A top-level comment and its replies. Replies arrive with the listing as a
// count only; opening the thread fetches the comment on its own.
const CommentRow: FC<RowProps> = ({
  comment,
  currentUserId,
  isExpanded,
  onExpand,
  onReply,
  onDelete,
}) => {
  const t = useTranslations();
  const needsFetch = isExpanded && comment.replies === undefined;
  const { data: detail, isFetching } = useComment(comment.id, {
    enabled: needsFetch,
  });
  const replies = comment.replies ?? (isExpanded ? detail?.replies : undefined);
  const hasReplies = comment.replyCount > 0 || (replies?.length ?? 0) > 0;

  return (
    <li className={styles.row.container}>
      <CommentBody
        comment={comment}
        size="md"
        onReply={onReply}
        onDelete={comment.author.id === currentUserId ? onDelete : undefined}
      />

      {hasReplies && (
        <div className={styles.row.replies}>
          {replies ? (
            replies.map((reply) => (
              <CommentBody
                key={reply.id}
                comment={reply}
                size="sm"
                onDelete={
                  reply.author.id === currentUserId ? onDelete : undefined
                }
              />
            ))
          ) : (
            <button
              type="button"
              className={styles.row.expand}
              onClick={onExpand}
            >
              <span className={styles.row.expandLine} />
              {t('comments.replies', { count: comment.replyCount })}
              {isFetching && <Spinner className="h-3 w-3" />}
            </button>
          )}
        </div>
      )}
    </li>
  );
};

interface BodyProps {
  comment: Comment;
  size: 'sm' | 'md';
  // Missing on replies: nesting stops at one level.
  onReply?: () => void;
  // Missing on other people's comments: only the author deletes their own.
  onDelete?: (comment: Comment) => void;
}

const CommentBody: FC<BodyProps> = ({ comment, size, onReply, onDelete }) => {
  const t = useTranslations();
  const format = useFormatter();
  // next-intl wants "now" passed in, so the server and browser agree.
  const now = useNow();
  const [isConfirmingDelete, setConfirmingDelete] = useState(false);

  return (
    <div className={styles.comment.container}>
      <MediaAvatar
        size={size === 'sm' ? 'xxs' : 'xs'}
        mediaId={comment.author.profileImage?.id}
        initials={initialsOf(comment.author.displayName)}
        colorKey={comment.author.id}
        alt=""
      />
      <div className={styles.comment.main}>
        <div className={styles.comment.meta}>
          <span className={styles.comment.author}>
            {comment.author.displayName}
          </span>
          <span className={styles.comment.time}>
            {format.relativeTime(new Date(comment.createdAt), now)}
          </span>
          {isCommentEdited(comment) && (
            <span className={styles.comment.time}>{t('comments.edited')}</span>
          )}
        </div>
        <p className={styles.comment.content}>{comment.content}</p>

        {isConfirmingDelete ? (
          <div className={styles.confirm.container}>
            <p className={styles.confirm.title}>{t('comments.deleteTitle')}</p>
            <p className={styles.confirm.message}>
              {t('comments.deleteMessage')}
            </p>
            <div className={styles.confirm.actions}>
              <button
                type="button"
                className={styles.comment.action}
                onClick={() => setConfirmingDelete(false)}
              >
                {t('common.cancel')}
              </button>
              <button
                type="button"
                className={styles.confirm.delete}
                onClick={() => {
                  setConfirmingDelete(false);
                  onDelete?.(comment);
                }}
              >
                {t('common.delete')}
              </button>
            </div>
          </div>
        ) : (
          (onReply || onDelete) && (
            <div className={styles.comment.actions}>
              {onReply && (
                <button
                  type="button"
                  className={styles.comment.action}
                  onClick={onReply}
                >
                  {t('comments.reply')}
                </button>
              )}
              {onDelete && (
                <button
                  type="button"
                  className={styles.comment.action}
                  onClick={() => setConfirmingDelete(true)}
                >
                  {t('common.delete')}
                </button>
              )}
            </div>
          )
        )}
      </div>
    </div>
  );
};

const styles = {
  outer: 'fixed inset-0 z-10 w-screen overflow-y-auto',
  inner: 'flex min-h-full items-center justify-center p-3 sm:p-4',
  panel: classNames(
    'flex w-full max-w-[520px] flex-col overflow-hidden rounded-2xl bg-surface-card text-ink-primary shadow-xl',
    'transition-all data-closed:opacity-0 data-closed:scale-95 data-enter:duration-200 data-leave:duration-150',
  ),
  header: {
    container:
      'flex items-center justify-between gap-3 border-b border-hairline px-5 py-4',
    titleWrap: 'flex items-baseline gap-2',
    title: 'text-base font-bold text-ink-primary',
    count: 'text-sm font-medium tabular-nums text-ink-secondary',
    close:
      'flex h-8 w-8 items-center justify-center rounded-full text-ink-secondary hover:bg-surface-field',
  },
  body: 'h-[380px] overflow-y-auto',
  list: 'py-2',
  empty: {
    container:
      'flex h-full flex-col items-center justify-center gap-1 px-8 text-center',
    icon: 'mb-2 text-3xl text-ink-tertiary',
    title: 'text-sm font-medium text-ink-secondary',
    subtitle: 'text-xs text-ink-tertiary',
    retry:
      'mt-3 rounded-full bg-surface-field px-4 py-1.5 text-sm font-semibold text-ink-primary hover:bg-ink-secondary/15',
  },
  banner: {
    container:
      'flex items-center gap-2 bg-red-500/10 py-1 pl-4 pr-2 text-xs text-ink-secondary',
    icon: 'text-red-500',
    text: 'flex-1',
    close:
      'flex h-7 w-7 items-center justify-center rounded-full text-ink-secondary hover:bg-ink-secondary/10',
  },
  composer: {
    container: 'border-t border-hairline px-4 py-3',
    replying: 'mb-2 flex items-center gap-1.5 text-xs text-ink-secondary',
    cancelReply:
      'flex h-5 w-5 items-center justify-center rounded-full hover:bg-ink-secondary/10',
    row: 'flex items-end gap-2.5',
    input: classNames(
      'max-h-28 min-h-9 flex-1 resize-none rounded-2xl border border-stroke bg-surface-field px-3.5 py-2 text-sm text-ink-primary',
      'placeholder:text-ink-tertiary focus:border-accent-ink focus:outline-hidden',
    ),
    send: 'flex h-9 w-9 flex-none items-center justify-center disabled:cursor-default',
  },
  row: {
    container: 'px-5 py-2.5',
    replies: 'mt-3 space-y-3 pl-10',
    expand:
      'flex items-center gap-2 py-1 text-xs font-medium text-ink-secondary hover:text-ink-primary',
    expandLine: 'inline-block h-px w-5 bg-ink-secondary/35',
  },
  comment: {
    container: 'flex items-start gap-2.5',
    main: 'min-w-0 flex-1',
    meta: 'flex flex-wrap items-baseline gap-1.5',
    author: 'text-xs font-semibold text-ink-primary',
    time: 'text-[11px] text-ink-tertiary',
    content: 'mt-0.5 whitespace-pre-wrap break-words text-sm text-ink-primary',
    actions: 'mt-1 flex items-center gap-4',
    action:
      'py-0.5 text-xs font-medium text-ink-secondary hover:text-ink-primary',
  },
  confirm: {
    container: 'mt-2 rounded-xl bg-surface-field px-3 py-2',
    title: 'text-xs font-semibold text-ink-primary',
    message: 'text-xs text-ink-secondary',
    actions: 'mt-1.5 flex items-center gap-4',
    delete: 'py-0.5 text-xs font-semibold text-red-500 hover:text-red-600',
  },
};
